import { useEffect, useRef, RefObject } from 'react';

type UseClickOutsideOptions = {
  enabled?: boolean;
  eventType?: 'mousedown' | 'click' | 'touchstart';
  /**
   * Skip firing the callback when the event target lives inside an element
   * matching this CSS selector. Useful for portal-rendered children — the
   * AnchoredPortal dropdown sits at document.body, not inside the wrapper
   * ref, so without this the dropdown closes on mousedown before the row's
   * click handler can dispatch a selection.
   */
  ignoreSelector?: string;
};

export function useClickOutside<T extends HTMLElement>(
  callback: () => void,
  options: UseClickOutsideOptions = {}
): RefObject<T> {
  const { enabled = true, eventType = 'mousedown', ignoreSelector } = options;

  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!ref.current) return;
      const target = event.target as Element | null;

      if (target && ignoreSelector && target.closest(ignoreSelector)) {
        return;
      }
      if (!ref.current.contains(target as Node)) {
        callback();
      }
    };

    document.addEventListener(eventType, handleClickOutside);

    return () => {
      document.removeEventListener(eventType, handleClickOutside);
    };
  }, [callback, enabled, eventType, ignoreSelector]);

  return ref;
}